import { useState, useEffect, useRef } from "react";
import { useWebSocket } from "@/context/WebSocketContext";
import { useNotification } from "@/context/NotificationContext";
import { WS_CHANNELS } from "@/utils/websocket";
import { wsLog } from "@/utils/debug";

interface TabConfig {
  channel: string;
  notificationTitle: (data: Record<string, any>) => string;
  notificationMessage?: (data: Record<string, any>) => string;
  instanceIdField?: string;
}

// Remove the old getWebSocketBaseUrl and WS_CHANNELS, use imported WS_CHANNELS instead
const TAB_CONFIGS: Record<string, TabConfig> = {
  channel: {
    channel: WS_CHANNELS.CHAT,
    notificationTitle: (data: Record<string, any>) => "New Message",
    notificationMessage: (data: Record<string, any>) => data.content,
    instanceIdField: "pod_instance_id",
  },
  activity: {
    channel: WS_CHANNELS.ACTIVITY,
    notificationTitle: (data: Record<string, any>) => "New Activity",
    notificationMessage: (data: Record<string, any>) => data.activity_type,
    instanceIdField: "pod_instance_id",
  },
  documents: {
    channel: WS_CHANNELS.DOCUMENTS,
    notificationTitle: (data: Record<string, any>) => "Document Updated",
    notificationMessage: (data: Record<string, any>) =>
      `${data.display_name || "A document"} was updated`,
    instanceIdField: "pod_instance_id",
  },
  tasks: {
    channel: WS_CHANNELS.TASKS,
    notificationTitle: (data: Record<string, any>) => "Task Updated",
    notificationMessage: (data: Record<string, any>) => data.title,
    instanceIdField: "pod_instance_id",
  },
  data: {
    channel: WS_CHANNELS.DATA,
    notificationTitle: (data: Record<string, any>) => "Data Updated",
    notificationMessage: (data: Record<string, any>) => "New data extracted",
    instanceIdField: "pod_instance_id",
  },
  portfolios: {
    channel: WS_CHANNELS.PORTFOLIOS,
    notificationTitle: (data: Record<string, any>) => "Portfolio Updated",
    notificationMessage: (data: Record<string, any>) =>
      "Portfolio data updated",
    instanceIdField: "pod_instance_id",
  },
};

// Additional config for system tasks
const SYSTEM_TASKS_CONFIG = {
  channel: WS_CHANNELS.SYSTEM_TASKS,
  notificationTitle: (data: Record<string, any>) => "System Task Update",
  notificationMessage: (data: Record<string, any>) => data.description,
  instanceIdField: "pod_instance_id",
};

export function usePodInstanceTabNotifications(
  instanceId: string,
  activeTab: string
) {
  const { lastMessage, subscribe, unsubscribe, status, onMessage } = useWebSocket();
  const notification = useNotification();
  const [tabNotifications, setTabNotifications] = useState<
    Record<string, boolean>
  >({});
  const processedMessageIds = useRef<Set<string>>(new Set());
  const subscriptionsRef = useRef<string[]>([]);
  const isInitialMountRef = useRef(true);

  // Subscribe to all channels on mount with a slight delay to avoid connection issues
  useEffect(() => {
    const subscribeToChannels = () => {
      // Collect channels, but avoid opening channel/chat unless the Channel tab is active
      const allChannels = [
        ...Object.entries(TAB_CONFIGS)
          .filter(([tabId]) =>
            tabId === "channel" ? activeTab === "channel" : true
          )
          .map(([, config]) => config.channel),
        SYSTEM_TASKS_CONFIG.channel,
      ];

      // Track subscribed channels
      subscriptionsRef.current = allChannels;

      // Subscribe to each channel with a small delay between each
      allChannels.forEach((channel, index) => {
        setTimeout(() => {
          // Check if the component is still mounted and channel is valid before subscribing
          if (subscriptionsRef.current.includes(channel)) {
            subscribe(channel);
          }
        }, index * 50); // Small delay between each subscription
      });
    };

    // When component first mounts, delay subscription to avoid race conditions
    // with WebSocket initialization after page refresh
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      const timer = setTimeout(subscribeToChannels, 300);
      return () => clearTimeout(timer);
    } else {
      // On subsequent re-renders, subscribe immediately
      subscribeToChannels();
    }

    // Cleanup subscriptions on unmount or when dependencies change
    return () => {
      subscriptionsRef.current.forEach((channel) => {
        unsubscribe(channel);
      });
      subscriptionsRef.current = [];
    };
  }, [subscribe, unsubscribe, instanceId, activeTab]);

  // Handle incoming messages using per-channel listeners to avoid drops
  useEffect(() => {
    const offs: Array<() => void> = [];

    const attach = (
      tabId: string,
      config: TabConfig & { channel: string }
    ) => {
      const off = onMessage(config.channel, (raw: any) => {
        try {
          // Unwrap possible WebSocketMessage payloads
          const baseMsg = typeof raw === "string" ? JSON.parse(raw) : raw;
          const msg = baseMsg && baseMsg.payload ? baseMsg.payload : baseMsg;
          // Ignore handshake/connected and any message without payload
          if (!msg || !msg.operation || !msg.full_document) {
            wsLog(
              "[PodTabNotif] Skip - missing payload/full_document",
              tabId,
              { channel: config.channel, raw: baseMsg }
            );
            return;
          }

          const fullDoc = msg.full_document;

          const messageId = `${config.channel}-${
            fullDoc.id || fullDoc._id || fullDoc.document_id || "noid"
          }-${msg.operation}-${fullDoc.updated_at || fullDoc.timestamp || ""}`;
          if (processedMessageIds.current.has(messageId)) return;
          processedMessageIds.current.add(messageId);

          // Robust instance id extraction
          const preferredField = config.instanceIdField || "instance_id";
          const docInstanceId =
            fullDoc[preferredField] ||
            fullDoc.instance_id ||
            fullDoc.pod_instance_id ||
            fullDoc.resource_id ||
            fullDoc.context_id ||
            fullDoc.entity_id;

          // Trace the message before filtering by instance
          wsLog(
            "[PodTabNotif] Message",
            tabId,
            {
              channel: config.channel,
              op: msg.operation,
              id: fullDoc._id || fullDoc.id || msg.document_id,
              docInstanceId,
              expectedInstanceId: instanceId,
            }
          );

          if (String(docInstanceId) !== String(instanceId)) {
            wsLog(
              "[PodTabNotif] Skip message - instance mismatch",
              tabId,
              {
                expected: instanceId,
                got: docInstanceId,
                fields: {
                  [preferredField]: fullDoc[preferredField],
                  instance_id: fullDoc.instance_id,
                  pod_instance_id: fullDoc.pod_instance_id,
                  resource_id: fullDoc.resource_id,
                  context_id: fullDoc.context_id,
                  entity_id: fullDoc.entity_id,
                },
              }
            );
            return;
          }

          if (tabId === "channel") {
            const isCurrentUser =
              fullDoc.sender?.id === "current-user" ||
              fullDoc.worker_id === "current-user" ||
              fullDoc.role === "user" ||
              fullDoc.sender?.type === "user";
            if (isCurrentUser) return;
          }

          if (activeTab !== tabId) {
            wsLog(
              "[PodTabNotif] Dot set",
              tabId,
              {
                channel: config.channel,
                op: msg.operation,
                id: fullDoc._id || fullDoc.id || msg.document_id,
              }
            );
            setTabNotifications((prev) => ({ ...prev, [tabId]: true }));
          } else {
            wsLog(
              "[PodTabNotif] Active tab received message; not setting dot",
              tabId,
              { op: msg.operation }
            );
          }
        } catch {
          // console.error(`Error processing ${tabId} message:`, error);
        }
      });
      offs.push(off);
    };

    Object.entries(TAB_CONFIGS).forEach(([tabId, config]) => attach(tabId, config));

    // System tasks
    offs.push(
      onMessage(SYSTEM_TASKS_CONFIG.channel, (raw: any) => {
        try {
          const msg = typeof raw === "string" ? JSON.parse(raw) : raw;
          if (!msg || !msg.operation || !msg.full_document) return;
          const fullDoc = msg.full_document;
          const messageId = `${SYSTEM_TASKS_CONFIG.channel}-${
            fullDoc.id || fullDoc._id || fullDoc.document_id || "noid"
          }-${msg.operation}-${fullDoc.updated_at || fullDoc.timestamp || ""}`;
          if (processedMessageIds.current.has(messageId)) return;
          processedMessageIds.current.add(messageId);
          const idField = SYSTEM_TASKS_CONFIG.instanceIdField || "instance_id";
          if (fullDoc[idField] === instanceId && activeTab !== "channel") {
            setTabNotifications((prev) => ({ ...prev, channel: true }));
          }
        } catch {
          // console.error("Error processing system task message:", error);
        }
      })
    );

    const cleanup = setTimeout(() => {
      processedMessageIds.current.clear();
    }, 60000);

    return () => {
      offs.forEach((off) => off());
      clearTimeout(cleanup);
    };
  }, [onMessage, activeTab, instanceId]);

  // Fallback: also process the latest message snapshot from context per channel.
  // This ensures we don't miss events if a per-channel listener was attached late.
  useEffect(() => {
    try {
      Object.entries(TAB_CONFIGS).forEach(([tabId, config]) => {
        const raw = lastMessage?.get(config.channel) as any;
        if (!raw) return;
        const baseMsg = typeof raw === "string" ? JSON.parse(raw) : raw;
        const msg = baseMsg && baseMsg.payload ? baseMsg.payload : baseMsg;
        if (!msg || !msg.operation || !msg.full_document) return;

        const fullDoc = msg.full_document;
        const messageId = `${config.channel}-${
          fullDoc.id || fullDoc._id || fullDoc.document_id || "noid"
        }-${msg.operation}-${fullDoc.updated_at || fullDoc.timestamp || ""}`;
        if (processedMessageIds.current.has(messageId)) return;
        processedMessageIds.current.add(messageId);

        const preferredField = config.instanceIdField || "instance_id";
        const docInstanceId =
          fullDoc[preferredField] ||
          fullDoc.instance_id ||
          fullDoc.pod_instance_id ||
          fullDoc.resource_id ||
          fullDoc.context_id ||
          fullDoc.entity_id;
        if (String(docInstanceId) !== String(instanceId)) return;

        if (tabId === "channel") {
          const isCurrentUser =
            fullDoc.sender?.id === "current-user" ||
            fullDoc.worker_id === "current-user" ||
            fullDoc.role === "user" ||
            fullDoc.sender?.type === "user";
          if (isCurrentUser) return;
        }

        if (activeTab !== tabId) {
          setTabNotifications((prev) => ({ ...prev, [tabId]: true }));
        }
      });
    } catch {
      // Silently ignore - message parsing may fail with malformed data
    }
  }, [lastMessage, activeTab, instanceId]);

  // Function to clear notification for a tab
  const clearTabNotification = (tabId: string) => {
    setTabNotifications((prev) => ({ ...prev, [tabId]: false }));
  };

  return {
    tabNotifications,
    clearTabNotification,
  };
}
